import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { WEBOT_SERVER_ENDPOINT } from 'logic/Consts';
import type { REST_schema } from '../@types/sharedSchema';

/**
 * Client for the server's Postgres-backed listings search
 * (POST /api/v1/listings-search/*) — the Algolia replacement.
 */

/**
 * The Algolia → Postgres migration switch.
 *
 * `false` (step 0): searches keep going to Algolia exactly as before, while the
 * Firestore→Postgres sync runs in the background and is watched for a few hours
 * (`npx ts-node adHocOperations/check-listings-pg-sync-health.ts` in
 * webot-server gives the go/no-go). Deploying with the mirror out of sync would
 * silently show customers a stale or incomplete market.
 *
 * `true` (step 1): every search, facet and picker lookup runs against the
 * server API, and the browser never mints an Algolia secured key — that key
 * handed the client the whole scraped corpus, seller phone numbers included.
 *
 * Both code paths exist until step 2 deletes the Algolia half, so switching
 * either way is this one line.
 */
export const USE_POSTGRES_LISTINGS_SEARCH = true;

export const isPgSearchEnabled = (): boolean => USE_POSTGRES_LISTINGS_SEARCH;

const getIdToken = async (): Promise<string> => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('listingsSearchApi: no authenticated user');
  return user.getIdToken();
};

const request = async <T>(
  path: string,
  init: { method: 'GET' | 'POST'; body?: unknown }
): Promise<T> => {
  const idToken = await getIdToken();
  const response = await fetch(`${WEBOT_SERVER_ENDPOINT}/api/v1/listings-search${path}`, {
    method: init.method,
    headers: {
      Authorization: idToken,
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {})
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined
  });
  if (!response.ok) {
    throw new Error(`listings-search ${init.method} ${path} failed with status ${response.status}`);
  }
  return (await response.json()) as T;
};

export const postListingsSearch = async (
  body: REST_schema.ListingsSearchRequest
): Promise<REST_schema.ListingsSearchResponse> => request('/search', { method: 'POST', body });

export const fetchNamesFacet = async (
  facet: 'agency-names' | 'seller-names',
  query: string,
  origin?: string
): Promise<REST_schema.ListingsNamesFacetResponse> => {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (origin) params.set('origin', origin);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return request(`/facets/${facet}${suffix}`, { method: 'GET' });
};

/**
 * The listings-match page's self-report (see `listingsMatchHealth.ts`). Mirrors the server's
 * `listingsMatchHealthReportSchema` (`listingsMatchClientHealth.ts`); a report the server
 * refuses is a 400 and is simply lost — it is telemetry.
 */
export type ListingsMatchHealthReport = {
  kind: 'freeze' | 'previous_page_died' | 'search_stuck' | 'render_storm' | 'error';
  pageId: string;
  pageStartedAt: number;
  reportedAt: number;
  path?: string;
  gapMs?: number;
  lastBeatAt?: number;
  hiddenAtLastBeat?: boolean;
  wasDiscarded?: boolean;
  urlLength?: number;
  error?: string;
  device?: Record<string, string | number | boolean | null>;
  breadcrumbs: Array<{
    t: number;
    n: string;
    c?: number;
    d?: Record<string, string | number | boolean | null>;
  }>;
  longTasks?: Array<{ t: number; ms: number }>;
};

/** True when the server took it. Never throws. `keepalive` lets it outlive a closing tab. */
export const postListingsMatchHealth = async (
  report: ListingsMatchHealthReport
): Promise<boolean> => {
  try {
    const idToken = await getIdToken();
    const response = await fetch(`${WEBOT_SERVER_ENDPOINT}/api/v1/listings-search/client-health`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: idToken, 'Content-Type': 'application/json' },
      body: JSON.stringify(report)
    });
    return response.ok;
  } catch {
    return false;
  }
};
